import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router';
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Spinner, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { Meter, Stat } from './charts';
import {
  ago,
  displayState,
  formatDateTime,
  formatDuration,
  gb,
  type Incident,
  kindName,
  type Maintenance,
  type MonitoringForm,
  type MonitorStatus,
  percent,
  sourceName,
  type Summary,
  type TargetDetail,
  type TargetRow,
  toLocalInput,
} from './data';

const tab = ({ isActive }: { isActive: boolean }) => `-mb-px border-b-2 px-3 py-2 text-sm ${isActive ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`;

/** Secciones del módulo Monitoreo. */
export function MonitoringNav() {
  return (
    <nav aria-label={t('mon.secciones')} className="flex gap-1 overflow-x-auto overflow-y-hidden border-b border-slate-200">
      <NavLink to="/monitoreo" end className={tab}>
        {t('mon.estado')}
      </NavLink>
      <NavLink to="/monitoreo/servidor" className={tab}>
        {t('mon.servidor')}
      </NavLink>
      <NavLink to="/monitoreo/incidentes" className={tab}>
        {t('mon.incidentes')}
      </NavLink>
      <NavLink to="/monitoreo/mantenimientos" className={tab}>
        {t('mon.mantenimientos')}
      </NavLink>
    </nav>
  );
}

export const useSummary = () => useQuery({ queryKey: ['monitoreo', 'resumen'], queryFn: () => api<Summary>('/api/monitoreo'), refetchInterval: 30_000, placeholderData: (prev) => prev });

// --- Estado ---

type Filter = MonitorStatus | 'todos';

/** Portada: qué está caído, el servidor, los incidentes abiertos y la lista de sitios con su disponibilidad. */
export function MonitoringPage() {
  const summary = useSummary();
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const data = summary.data;
  const filter = (params.get('mostrar') as Filter | null) ?? 'activo';
  const setFilter = (f: Filter) => {
    const next = new URLSearchParams(params);
    if (f === 'activo') next.delete('mostrar');
    else next.set('mostrar', f);
    setParams(next, { replace: true });
  };

  if (!data) {
    return (
      <div className="space-y-5">
        <PageHeader title={t('mon.titulo')} description={t('mon.descripcion')} />
        <MonitoringNav />
        {summary.error ? <Alert>{errorMessage(summary.error)}</Alert> : <Spinner label={t('comun.cargando')} />}
      </div>
    );
  }

  const down = data.targets.filter((x) => x.status === 'activo' && x.state === 'caido');
  const watched = data.targets.filter((x) => x.status === 'activo');
  const active = data.maintenance.filter((m) => m.state === 'en_curso');

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('mon.titulo')}
        description={t('mon.descripcion')}
        actions={
          data.can.manage && !adding ? (
            <Button
              onClick={() => {
                setAdding(true);
                setNotice(null);
              }}
            >
              {t('mon.agregar')}
            </Button>
          ) : undefined
        }
      />
      <MonitoringNav />

      {down.length > 0 ? (
        <Alert>{t('mon.hay_caidos', { n: down.length, nombres: down.map((d) => d.name).join(', ') })}</Alert>
      ) : (
        // Solo cuando todos ya respondieron: un sitio recién agregado (sin datos) todavía no dice nada.
        watched.length > 0 && watched.every((x) => x.state === 'arriba' || x.state === 'lento') && <Alert tone="success">{t('mon.todo_arriba')}</Alert>
      )}
      {active.length > 0 && <Alert tone="info">{t('mon.mant_en_curso_aviso', { lista: active.map((m) => `${m.target ?? t('mon.mant_todo')} (${m.reason})`).join('; ') })}</Alert>}
      {data.counts.porRevisar > 0 && filter !== 'por_revisar' && (
        <Alert tone="warning">
          {t('mon.hay_por_revisar', { n: data.counts.porRevisar })}{' '}
          <Button variant="link" className="font-medium" onClick={() => setFilter('por_revisar')}>
            {t('mon.ver_por_revisar')}
          </Button>
        </Alert>
      )}
      {notice && <Alert tone="success">{notice}</Alert>}

      {adding && (
        <Card title={t('mon.nuevo_titulo')}>
          <TargetForm
            onDone={(msg) => {
              setAdding(false);
              setNotice(msg);
            }}
            onCancel={() => setAdding(false)}
          />
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label={t('mon.cuenta_arriba')} value={data.counts.arriba} />
        <Stat label={t('mon.cuenta_lento')} value={data.counts.lento} />
        <Stat label={t('mon.cuenta_caido')} value={<span className={data.counts.caido > 0 ? 'text-red-700' : ''}>{data.counts.caido}</span>} />
        <Stat label={t('mon.cuenta_desconocido')} value={data.counts.desconocido} />
      </div>

      {data.server && (
        <Card
          title={
            <span className="flex flex-wrap items-baseline justify-between gap-2">
              <span>
                {t('mon.servidor_resumen')} · <span className="font-mono text-sm font-normal">{data.server.host.name}</span>
              </span>
              <Link to="/monitoreo/servidor" className="text-sm font-normal text-marca underline-offset-4 hover:underline">
                {t('mon.ver_servidor')}
              </Link>
            </span>
          }
        >
          {data.server.host.stale && (
            <div className="mb-3">
              <Alert tone="warning">{t('mon.servidor_sin_datos')}</Alert>
            </div>
          )}
          {data.server.latest && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Meter label={t('mon.cpu')} value={data.server.latest.cpu} />
              <Meter label={t('mon.memoria')} value={data.server.latest.memory} />
              {data.server.latest.swap !== null && <Meter label={t('mon.swap')} value={data.server.latest.swap} />}
              <Meter label={t('mon.disco')} value={data.server.latest.disk} sub={t('mon.libres', { gb: gb(data.server.latest.diskFreeBytes) })} />
            </div>
          )}
        </Card>
      )}

      {data.incidents.length > 0 && (
        <Card title={t('mon.incidentes')}>
          <IncidentList incidents={data.incidents} canAcknowledge={data.can.acknowledge} />
        </Card>
      )}

      <TargetsTable targets={data.targets} filter={filter} setFilter={setFilter} canManage={data.can.manage} onNotice={setNotice} />
    </div>
  );
}

function TargetsTable({
  targets,
  filter,
  setFilter,
  canManage,
  onNotice,
}: {
  targets: TargetRow[];
  filter: Filter;
  setFilter: (f: Filter) => void;
  canManage: boolean;
  onNotice: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = search.trim().toLowerCase();
  const rows = targets.filter((x) => (filter === 'todos' || x.status === filter) && (!q || x.name.toLowerCase().includes(q) || x.url.includes(q)));
  // Lo caído primero, después lo lento, y el resto por nombre.
  const order = { caido: 0, lento: 1, desconocido: 2, arriba: 3 } as const;
  rows.sort((a, b) => order[a.state] - order[b.state] || a.name.localeCompare(b.name));
  const selectable = canManage && filter === 'por_revisar';
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  /** Vigilar los seleccionados, o no vigilarlos (pasan a pausados y dejan de estar por revisar). */
  async function decide(watch: boolean) {
    setBusy(true);
    setError(null);
    try {
      const ids = { json: { ids: [...selected] } };
      if (watch) onNotice(t('mon.activados', { n: (await api<{ activated: number }>('/api/monitoreo/sitios/activar', ids)).activated }));
      else onNotice(t('mon.no_vigilados', { n: (await api<{ dismissed: number }>('/api/monitoreo/sitios/no-vigilar', ids)).dismissed }));
      setSelected(new Set());
      await qc.invalidateQueries({ queryKey: ['monitoreo'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-md border border-slate-200 bg-white">
      <h2 className="px-5 pt-5 text-base font-semibold text-slate-900">{t('mon.sitios')}</h2>
      <div className="flex flex-wrap items-end gap-3 px-5 pb-4 pt-3">
        <div className="w-48">
          <Select id="mon-filtro" label={t('mon.filtro')} value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="activo">{t('mon.filtro_activos')}</option>
            <option value="por_revisar">{t('mon.filtro_por_revisar')}</option>
            <option value="pausado">{t('mon.filtro_pausados')}</option>
            <option value="todos">{t('mon.filtro_todos')}</option>
          </Select>
        </div>
        <div className="w-64">
          <Field id="mon-buscar" type="search" label={t('mon.buscar')} placeholder={t('mon.buscar_ayuda')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {selectable && selected.size > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => void decide(true)}>
              {t('mon.activar_seleccion', { n: selected.size })}
            </Button>
            <Button variant="secondary" disabled={busy} title={t('mon.no_vigilar_ayuda')} onClick={() => void decide(false)}>
              {t('mon.no_vigilar_seleccion', { n: selected.size })}
            </Button>
          </div>
        )}
      </div>
      {error && (
        <div className="px-5 pb-3">
          <Alert>{error}</Alert>
        </div>
      )}
      {targets.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-slate-500">{t('mon.vacio_inicio')}</p>
      ) : rows.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-slate-500">{t('mon.vacio')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-y border-slate-200 bg-slate-50 text-xs text-slate-500">
              <tr>
                {selectable && (
                  <th className="w-10 px-4 py-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-marca"
                      aria-label={t('mon.seleccionar_todos')}
                      checked={rows.every((r) => selected.has(r.id))}
                      onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
                    />
                  </th>
                )}
                <th className="px-4 py-2 font-medium">{t('mon.col_sitio')}</th>
                <th className="px-4 py-2 font-medium">{t('mon.col_estado')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('mon.col_24h')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('mon.col_7d')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('mon.col_30d')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('mon.col_latencia')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('mon.col_certificado')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const s = displayState(r);
                return (
                  <tr key={r.id} className={r.status === 'activo' && r.state === 'caido' ? 'bg-red-50/60' : ''}>
                    {selectable && (
                      <td className="px-4 py-2.5">
                        <input type="checkbox" className="h-4 w-4 accent-marca" aria-label={t('mon.seleccionar', { nombre: r.name })} checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                      </td>
                    )}
                    <td className="max-w-xs px-4 py-2.5">
                      <Link to={`/monitoreo/sitios/${r.id}`} className="font-medium text-slate-900 underline-offset-4 hover:underline">
                        {r.name}
                      </Link>
                      <span className="block truncate text-xs text-slate-500">
                        {r.url}
                        {r.app && ` · ${r.app.name}`}
                      </span>
                      {r.missingSince && <span className="block text-xs text-amber-900">{t('mon.ya_no_esta', { origen: sourceName(r.source) })}</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={s.tone}>{s.label}</Badge>
                      {r.status === 'activo' && r.state === 'caido' && r.lastError && <span className="mt-0.5 block max-w-56 text-xs text-red-800">{r.lastError}</span>}
                      {r.status === 'activo' && r.stateSince && r.state !== 'desconocido' && <span className="block text-xs text-slate-500">{ago(r.stateSince)}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{percent(r.uptime.day, 2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{percent(r.uptime.week, 2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{percent(r.uptime.month, 2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{r.uptime.latencyMs !== null ? t('mon.ms', { n: r.uptime.latencyMs }) : t('mon.sin_datos')}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums ${r.certDays !== null && r.certDays <= 14 ? 'font-medium text-amber-900' : ''}`}>
                      {r.certDays === null ? t('mon.sin_datos') : r.certDays < 0 ? t('mon.cert_vencido') : t('mon.cert_dias', { n: r.certDays })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- Alta y edición de un sitio ---

/** Formulario de un sitio: nuevo (sin `target`) o edición. */
export function TargetForm({ target, onDone, onCancel }: { target?: TargetDetail; onDone: (msg: string) => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const form = useQuery({ queryKey: ['monitoreo', 'formulario'], queryFn: () => api<MonitoringForm>('/api/monitoreo/formulario') });
  const [name, setName] = useState(target?.name ?? '');
  const [url, setUrl] = useState(target?.url ?? 'https://');
  const [appEntryId, setAppEntryId] = useState(target?.app?.id ?? '');
  const [ownerId, setOwnerId] = useState(target?.ownerId ?? '');
  const [min, setMin] = useState(String(target?.expectedStatusMin ?? 200));
  const [max, setMax] = useState(String(target?.expectedStatusMax ?? 399));
  const [keyword, setKeyword] = useState(target?.keyword ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name: name.trim(),
      url: url.trim(),
      appEntryId: appEntryId || null,
      ownerId: ownerId || null,
      expectedStatusMin: Number(min),
      expectedStatusMax: Number(max),
      keyword: keyword.trim() || null,
    };
    try {
      if (target) await api(`/api/monitoreo/sitios/${target.id}`, { method: 'PATCH', json: body });
      else await api('/api/monitoreo/sitios', { json: body });
      await qc.invalidateQueries({ queryKey: ['monitoreo'] });
      onDone(target ? t('mon.guardado') : t('mon.creado'));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!form.data) return form.error ? <Alert>{errorMessage(form.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  return (
    <form onSubmit={(e) => void submit(e)} className="grid gap-3 md:grid-cols-2">
      {error && (
        <div className="md:col-span-2">
          <Alert>{error}</Alert>
        </div>
      )}
      <Field id="mon-nombre" label={t('mon.campo_nombre')} required minLength={2} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
      <div className="space-y-1">
        <Field id="mon-url" type="url" label={t('mon.campo_url')} required maxLength={500} value={url} onChange={(e) => setUrl(e.target.value)} />
        <p className="text-xs text-slate-500">{t('mon.campo_url_ayuda')}</p>
      </div>
      <Select
        id="mon-app"
        label={t('mon.campo_app')}
        value={appEntryId}
        onChange={(e) => {
          setAppEntryId(e.target.value);
          // Un sitio nuevo toma la dirección de su aplicación si todavía no se escribió ninguna.
          const app = form.data.apps.find((a) => a.id === e.target.value);
          if (!target && app?.url && (url === 'https://' || !url)) setUrl(app.url);
          if (!target && app && !name) setName(app.name);
        }}
      >
        <option value="">{t('mon.campo_app_ninguna')}</option>
        {form.data.apps.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </Select>
      <Select id="mon-responsable" label={t('mon.campo_responsable')} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
        <option value="">{t('mon.campo_responsable_ninguno')}</option>
        {form.data.owners.map((o) => (
          <option key={o.id} value={o.id}>
            {o.fullName}
          </option>
        ))}
      </Select>
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium text-slate-700">{t('mon.campo_codigos')}</legend>
        <div className="flex gap-3">
          <Field id="mon-min" type="number" min={100} max={599} label={t('mon.campo_codigo_min')} value={min} onChange={(e) => setMin(e.target.value)} />
          <Field id="mon-max" type="number" min={100} max={599} label={t('mon.campo_codigo_max')} value={max} onChange={(e) => setMax(e.target.value)} />
        </div>
      </fieldset>
      <div className="space-y-1">
        <Field id="mon-texto" label={t('mon.campo_texto')} maxLength={200} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        <p className="text-xs text-slate-500">{t('mon.campo_texto_ayuda')}</p>
      </div>
      <div className="flex gap-2 md:col-span-2">
        <Button type="submit" disabled={busy || name.trim().length < 2}>
          {target ? t('comun.guardar') : t('mon.crear')}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </form>
  );
}

// --- Incidentes ---

export function IncidentList({ incidents, canAcknowledge }: { incidents: Incident[]; canAcknowledge: boolean }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  async function acknowledge(id: string) {
    setError(null);
    try {
      await api(`/api/monitoreo/incidentes/${id}/tomar`, { method: 'POST' });
      await qc.invalidateQueries({ queryKey: ['monitoreo'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  }
  if (incidents.length === 0) return <p className="text-sm text-slate-500">{t('mon.inc_vacio')}</p>;
  return (
    <div className="space-y-2">
      {error && <Alert>{error}</Alert>}
      <ul className="divide-y divide-slate-100" aria-label={t('mon.incidentes')}>
        {incidents.map((i) => (
          <li key={i.id} className="flex flex-wrap items-start justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">
                <span className="mr-2 align-middle">
                  <Badge tone={i.endedAt ? 'slate' : 'red'}>{kindName(i.kind)}</Badge>
                </span>
                <Link to={i.targetId ? `/monitoreo/sitios/${i.targetId}` : '/monitoreo/servidor'} className="underline-offset-4 hover:underline">
                  {i.title}
                </Link>
              </p>
              {i.detail && <p className="text-xs text-slate-600">{i.detail}</p>}
              <p className="text-xs text-slate-500">
                {formatDateTime(i.startedAt)} · {i.endedAt ? t('mon.inc_duracion', { d: formatDuration(i.durationMs) }) : t('mon.inc_lleva', { d: formatDuration(i.durationMs) })}
                {i.notifications > 0 && ` · ${t('mon.inc_avisos', { n: i.notifications })}`}
              </p>
            </div>
            <div className="text-right text-xs">
              {!i.endedAt && i.acknowledgedBy && <p className="font-medium text-slate-700">{t('mon.inc_tomado', { nombre: i.acknowledgedBy })}</p>}
              {!i.endedAt && !i.acknowledgedAt && canAcknowledge && (
                <Button variant="secondary" className="px-3 py-1.5" title={t('mon.inc_tomar_ayuda')} onClick={() => void acknowledge(i.id)}>
                  {t('mon.inc_tomar')}
                </Button>
              )}
              {i.endedAt && <p className="text-slate-500">{t('mon.inc_cerrado')}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function IncidentsPage() {
  const [open, setOpen] = useState(true);
  const q = useQuery({
    queryKey: ['monitoreo', 'incidentes', open],
    queryFn: () => api<{ incidents: Incident[]; can: { acknowledge: boolean } }>(`/api/monitoreo/incidentes?abiertos=${open}`),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
  return (
    <div className="space-y-5">
      <PageHeader title={t('mon.titulo')} description={t('mon.descripcion')} />
      <MonitoringNav />
      <div role="radiogroup" aria-label={t('mon.incidentes')} className="inline-flex rounded border border-slate-300 bg-white p-0.5">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            role="radio"
            aria-checked={open === v}
            onClick={() => setOpen(v)}
            className={`rounded-sm px-3 py-1 text-sm ${open === v ? 'bg-marca text-white' : 'text-slate-700 hover:bg-slate-100'}`}
          >
            {v ? t('mon.inc_abiertos') : t('mon.inc_todos')}
          </button>
        ))}
      </div>
      <Card>{!q.data ? q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Spinner label={t('comun.cargando')} /> : <IncidentList incidents={q.data.incidents} canAcknowledge={q.data.can.acknowledge} />}</Card>
    </div>
  );
}

// --- Mantenimientos ---

export function MaintenancePage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['monitoreo', 'mantenimientos'], queryFn: () => api<{ maintenance: Maintenance[]; can: { manage: boolean } }>('/api/monitoreo/mantenimientos') });
  const summary = useSummary();
  const now = new Date();
  const [targetId, setTargetId] = useState('');
  const [startsAt, setStartsAt] = useState(toLocalInput(now));
  const [endsAt, setEndsAt] = useState(toLocalInput(new Date(now.getTime() + 3_600_000)));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/monitoreo/mantenimientos', { json: { targetId: targetId || null, startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString(), reason: reason.trim() } });
      setReason('');
      await qc.invalidateQueries({ queryKey: ['monitoreo'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function end(id: string) {
    setError(null);
    try {
      await api(`/api/monitoreo/mantenimientos/${id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['monitoreo'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  const stateLabel = { programado: t('mon.mant_programado'), en_curso: t('mon.mant_en_curso'), terminado: t('mon.mant_terminado') };
  return (
    <div className="space-y-5">
      <PageHeader title={t('mon.titulo')} description={t('mon.mant_descripcion')} />
      <MonitoringNav />
      {error && <Alert>{error}</Alert>}
      <div className={`grid gap-5 ${list.data?.can.manage ? 'lg:grid-cols-[22rem_1fr]' : ''}`}>
        {list.data?.can.manage && (
          <Card title={t('mon.mant_nuevo')} className="self-start">
            <form onSubmit={(e) => void create(e)} className="space-y-3">
              <Select id="mant-sitio" label={t('mon.mant_sitio')} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                <option value="">{t('mon.mant_todo')}</option>
                {(summary.data?.targets ?? [])
                  .filter((x) => x.status === 'activo')
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </Select>
              <Field id="mant-inicio" type="datetime-local" label={t('mon.mant_inicio')} required value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              <Field id="mant-fin" type="datetime-local" label={t('mon.mant_fin')} required value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              <Textarea id="mant-motivo" rows={2} label={t('mon.mant_motivo')} placeholder={t('mon.mant_motivo_ayuda')} required minLength={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
              <Button type="submit" disabled={busy || reason.trim().length < 3}>
                {t('mon.mant_crear')}
              </Button>
            </form>
          </Card>
        )}
        <Card className="self-start p-0">
          {!list.data ? (
            <div className="p-5">{list.error ? <Alert>{errorMessage(list.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
          ) : list.data.maintenance.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">{t('mon.mant_vacio')}</p>
          ) : (
            <ul className="divide-y divide-slate-100" aria-label={t('mon.mantenimientos')}>
              {list.data.maintenance.map((m) => (
                <li key={m.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {m.target ?? t('mon.mant_todo')}
                      <span className="ml-2 align-middle">
                        <Badge tone={m.state === 'en_curso' ? 'amber' : m.state === 'programado' ? 'blue' : 'slate'}>{stateLabel[m.state]}</Badge>
                      </span>
                    </p>
                    <p className="text-xs text-slate-600">{m.reason}</p>
                    <p className="text-xs text-slate-500">
                      {formatDateTime(m.startsAt)} – {formatDateTime(m.endsAt)}
                      {m.createdBy && ` · ${t('mon.mant_por', { nombre: m.createdBy })}`}
                    </p>
                  </div>
                  {list.data.can.manage && m.state !== 'terminado' && (
                    <Button variant="link" className="text-sm" onClick={() => void end(m.id)}>
                      {m.state === 'en_curso' ? t('mon.mant_terminar') : t('mon.mant_cancelar')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

